import { AsyncIterableList } from '@fullstory/util/collections';
import { memoize } from 'lodash';
import { PageModel } from './Page';
import type { PageFetcherContext } from '../fetchers';
import { makePageLoader, PageLoader } from '../loaders';
import type { PagePlatform } from '../pagetypes';
import { LinearizedPageList, PageList } from './PageList';
import { currentPageAtTime } from '../utils/currentPageAtTime';
import { PageLifecycleState } from '@fullstory/client-api';

type PageSearchOptions = { isExact: true } | { isExact: false; currentTimeMs?: number };

/**
 * Represents a single inferred "tab" within a session. A tab is composed of an ordered set of pages, guaranteed to be
 * non-overlapping in time, and of a single type (i.e., web, android, ios).
 */
export class TabModel {
  private _pageLoaders: { [type: string]: PageLoader } = {};

  // NOTE: New PageModel instances must *only* be added to `_pages` from
  // addPages(), which is overridden by LinearizedTabModel.
  protected _pages = new PageList();

  constructor(
    protected _ctx: PageFetcherContext,
    public readonly tabId: string,
    pages: PageModel[],
  ) {
    this.addPages(pages);
  }

  get closed(): boolean {
    return [...this._pages].every(p => p.closed);
  }

  get pages(): AsyncIterableList<PageModel> {
    return this._pages;
  }

  get startTime(): number {
    return this._pages.startTime;
  }

  // endTime and when the last page finishes. Their can be gaps between pages start and stop times
  get endTime(): number {
    return this._pages.endTime;
  }

  addPages(pages: PageModel[]) {
    this._pages.append(pages);
  }

  // Number of pages in the session.
  pageCount(): number {
    return this._pages.length;
  }

  // Gets a page by index.
  page(idx: number): PageModel | undefined {
    return this._pages.get(idx);
  }

  lastPage(): PageModel | undefined {
    if (this._pages.length === 0) {
      return undefined;
    }
    return this._pages.get(this._pages.length - 1);
  }

  forEachPage(callback: (page: Readonly<PageModel>, idx?: number) => void): void {
    let i = 0;
    for (const page of this._pages) {
      callback(page, i++);
    }
  }

  findPage(
    pred: (page: Readonly<PageModel>, idx?: number) => boolean,
  ): Readonly<PageModel> | undefined {
    let i = 0;
    for (const page of this._pages) {
      if (pred(page, i++)) {
        return page;
      }
    }
    return undefined;
  }

  findLastPage(
    pred: (page: Readonly<PageModel>, idx?: number) => boolean,
  ): Readonly<PageModel> | null {
    for (let i = this._pages.length - 1; i >= 0; i--) {
      const page = this._pages.get(i)!;
      if (pred(page)) {
        return page;
      }
    }
    return null;
  }

  /**
   * Returns an ordered list of all pages that existed at a point in time
   */
  allPagesAtTime(absTime: number, opts: PageSearchOptions = { isExact: true }): PageModel[] {
    const pages: PageModel[] = [];

    if (opts.isExact) {
      // get all pages that existed at the specified time
      for (const page of this._pages.at(absTime)) {
        if (!page) {
          // sanity check
          continue;
        }
        const lifecycleState = page.lifecycleStateAt(absTime)?.value;

        if (lifecycleState !== PageLifecycleState.TERMINATED) {
          pages.push(page as PageModel);
        }
      }
    } else {
      // non-strict is used to get all pages that existed at the specified time or within the last 30 minutes
      const { currentTimeMs = new Date().getTime() } = opts;
      for (const page of this._pages.atOrBefore(absTime)) {
        if (!page) {
          // sanity check
          continue;
        }
        const lifecycleState = page.lifecycleStateAt(absTime)?.value;
        const thirtyMinutesInMs = 30 * 60 * 1000; // 30 minutes * 60 seconds * 1000 milliseconds === 30 minutes in milliseconds
        const thirtyMinutesAgoMs = new Date(currentTimeMs - thirtyMinutesInMs).getTime();

        if (
          lifecycleState !== PageLifecycleState.TERMINATED &&
          // if endTime ms timestamp is greater the 30 minutes ago ms timestamp then an event was recorded within the last 30 minutes
          page.localEndTime > thirtyMinutesAgoMs
        ) {
          pages.push(page as PageModel);
        }
      }
    }

    return pages;
  }

  pageIndexForId(pageId: string): number {
    return this._pages.indexForId(pageId);
  }

  /**
   * Look up the current page at a point in time for this tab. The current page
   * is the page that the end user was most recently viewing in this tab.
   * This method exists for special circumstances, but it's generally best
   * to use {@link SessionModel.currentPageAtTime} when you want the page that
   * was actually being viewed by the end user.
   * NOTE: The current page is NOT guaranteed to be active. In many cases, it may be passive or hidden.
   * [Page Lifecycle](https://developer.chrome.com/blog/page-lifecycle-api/#overview-of-page-lifecycle-states-and-events)
   */
  currentPageAtTime(
    absTime: number,
    opts: PageSearchOptions = { isExact: true },
  ): PageModel | undefined {
    const pages = this.allPagesAtTime(absTime, opts);
    return currentPageAtTime(pages, absTime);
  }

  // Returns the total number of events in all pages.
  eventCount(): number {
    let events = 0;
    this.forEachPage(page => (events += page.cookedEvents.length));
    return events;
  }

  // Gets the PageLoader instance associated with a given page, for loading raw page data.
  pageLoader(idx: number = 0): PageLoader<PagePlatform> {
    const pageAtIndex = this.page(idx);
    if (!pageAtIndex) {
      throw new Error(`Can't retrieve a PageLoader for an empty session at index of ${idx}`);
    }
    const type = pageAtIndex.type;
    if (!(type in this._pageLoaders)) {
      this._pageLoaders[type] = makePageLoader(this._ctx, type, this);
    }
    return this._pageLoaders[type]!;
  }

  /**
   * Server playback is enabled for tabs that consist entirely of server pages
   * TODO(jordanhurt) - evaluate if this property should be moved to {@link LinearizedTabModel}
   */
  isServerPlaybackEnabled = memoize(
    () => [...this._pages].every(p => p.isForPlatform('server')),
    () => this._pages.length,
  );

  destroy() {
    this._pages.close();
  }
}

/**
 * A "linearized" tab, where all the pages have been shoved apart to guarantee they're non-overlapping.
 * @deprecated
 * this class should be removed as soon as multi-tab support reaches general availability.
 */
export class LinearizedTabModel extends TabModel {
  // Cache the last pageIndexAtTime() result so it can be called repeatedly.
  //
  // TODO(jgw): Pre-index this._pages to make this more efficient in all
  // cases, maybe using the timeline range stuff.
  private _pageIndexAtTimeResult?: {
    numPages: number; // Invalidate cache if new pages are added.
    startTime: number;
    endTime: number;
    index: number;
  };

  // NOTE: New PageModel instances must *only* be added to `_pages` from
  // addPages(), which is overridden by LinearizedTabModel.
  protected _pages = new LinearizedPageList();

  get startTime(): number {
    return this._pages.get(0)?.startTime ?? 0;
  }
  // The timeline and event list don't yet deal cleanly with overlapping
  // pages (i.e., multiple tabs/windows) in a single session. As a
  // stopgap, override addPageDescs() to linearize the session by adjusting
  // overlapping pages' absolute times.
  //
  // TODO(sethf): This method is in an intermediate state. The 'absTimeDelta'
  // values we're computing below don't make sense as properties of individual
  // PageModels; they're properties of a PageModel *sequence*. In a future PR,
  // we should pull the code that computes ranges out of the Timeline class,
  // turn it into an independent RangeList data structure, and let SessionModel
  // own it. These 'absTimeDelta' values will fit in naturally as part of that
  // data structure (and indeed the ranges that Timeline computes already
  // include them).
  //
  // @Override
  addPages(pages: PageModel[]) {
    const lastPage = this.lastPage();
    let lastPageEndsAt = lastPage ? lastPage.startTime + lastPage.duration : 0;
    if (pages.length <= 0) {
      return;
    }
    const models = pages.map(page => {
      if (lastPageEndsAt !== undefined) {
        const delta = page.startTime < lastPageEndsAt ? lastPageEndsAt - page.startTime : 0;
        page.absTimeDelta = delta;
      }
      lastPageEndsAt = page.startTime + page.duration;
      return page;
    });
    this._pages.append(models);
    this._pageIndexAtTimeResult = undefined;
  }

  /** Finds the page that contains the given absolute time. */
  pageIndexAtTime(absTime: number): number {
    if (this._pageIndexAtTimeResult) {
      const c = this._pageIndexAtTimeResult;
      if (c.startTime <= absTime && absTime < c.endTime && this._pages.length == c.numPages) {
        return c.index;
      }
    }
    this._pageIndexAtTimeResult = undefined;

    let hasServerPage = false;

    // Find the first page that includes `absTime`, starting from the end.
    for (let i = this._pages.length - 1; i >= 0; i--) {
      const page = this._pages.get(i)!;
      // skip playback for server page if a session contains non-server pages
      if (!this.isServerPlaybackEnabled() && page.isForPlatform('server')) {
        hasServerPage = true;
        continue;
      }
      const { startTime, duration } = page;
      if (absTime < startTime) {
        continue;
      }
      if (duration == 0) {
        // `Page` has no events; assume it's a new page in a Live session
        // with no definite end time. (This function is called before events
        // get added to new live pages).
        //
        // We don't want to set _pageIndexAtTimeResult in this case, or we'll get
        // stuck on this page.
        return i;
      }

      const endTime = startTime + duration;
      if (absTime <= endTime) {
        this._pageIndexAtTimeResult = {
          startTime,
          endTime,
          numPages: this._pages.length,
          index: i,
        };
        return i;
      }
    }

    // Return the index before the server page
    if (!this.isServerPlaybackEnabled() && hasServerPage) {
      for (let i = this._pages.length - 1; i >= 0; i--) {
        const page = this._pages.get(i)!;
        if (page.isForPlatform('server')) {
          continue;
        }
        return i;
      }
    }

    return -1;
  }
}
